import { lazy, Suspense, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AlertCircleIcon, TrophyIcon } from "lucide-react";
import {
  formatCompact,
  formatNumber,
  type LeaderboardResponse,
} from "@scandi/shared";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { StandingsBoard } from "@/components/leaderboard/standings";
import { MoreVideos } from "@/components/leaderboard/more-videos";
import { cn } from "@/lib/utils";
import { PeriodBar } from "@/components/leaderboard/period-bar";
import { RangePicker } from "@/components/leaderboard/range-picker";
import { RefreshButton } from "@/components/leaderboard/refresh-button";
import { MetricChip } from "@/components/leaderboard/metric-chip";
import { RankMetricProvider, useMetrics } from "@/components/metrics";
import { DataFreshness } from "@/components/leaderboard/data-freshness";
import { useLeaderboard, usePeriods } from "@/hooks/use-leaderboard";
import { useRange } from "@/hooks/use-range-params";
import { useSession } from "@/hooks/use-session";
import type { RangeSelection } from "@/lib/api";
import { useErrorMessage } from "@/lib/errors";
import { prefetchRoute } from "@/lib/route-prefetch";

/**
 * A reading column and a sidebar, once the viewport can hold both.
 *
 * Shared by the page header and the content so the two stay aligned; defining
 * the tracks twice is how they drift apart.
 */
/** The strategist's payout card: hers and the admins' only, so its code is not in an editor's first paint. */
const LazyStrategistPayout = lazy(() =>
  import("@/components/strategist/strategist-payout").then((m) => ({ default: m.StrategistPayout })),
);
function StrategistPayout({ periodIndex }: { periodIndex: number | undefined }) {
  return (
    <Suspense fallback={<Skeleton className="h-64 w-full rounded-xl" />}>
      <LazyStrategistPayout periodIndex={periodIndex} />
    </Suspense>
  );
}

/**
 * Everything beside and below the standings loads in its own chunk: the first
 * load has a size budget (`scripts/check-size.ts`) and the standings are what
 * the page is opened for.
 */
const TopVariants = lazy(() =>
  import("@/components/leaderboard/top-variants").then((m) => ({ default: m.TopVariants })),
);
const NewWinners = lazy(() =>
  import("@/components/leaderboard/new-winners").then((m) => ({ default: m.NewWinners })),
);
const LazyPrizeCards = lazy(() =>
  import("@/components/leaderboard/prize-card").then((m) => ({ default: m.PrizeCards })),
);
function PrizeCards() {
  return (
    <Suspense fallback={<Skeleton className="h-96 w-full rounded-[24px]" />}>
      <LazyPrizeCards />
    </Suspense>
  );
}

const COLUMNS = "xl:grid xl:grid-cols-[minmax(0,1fr)_22rem] xl:gap-6 2xl:grid-cols-[minmax(0,1fr)_25rem] 2xl:gap-8";

export function LeaderboardPage() {
  const { t } = useTranslation();
  const describeError = useErrorMessage();
  const [range, setRange] = useRange();
  const { isAdmin, isStrategist, canSeeStrategist } = useSession();
  const { data, isLoading, isError, error } = useLeaderboard(range);
  const { data: periodData } = usePeriods();

  // The cards under the board (the prize, the new winners, the payout, the "AI" tags) read beside the board
  // rather than after it: each used to wait for the board to arrive before it was even mounted.
  const queryClient = useQueryClient();
  const { search } = useLocation();
  useEffect(() => {
    prefetchRoute(queryClient, `/leaderboard${search}`, { code: false });
  }, [queryClient, search]);

  return (
    <div className="flex flex-col gap-4">
      {/* Laid out on the same tracks as the content below so the title and the
          range picker sit over the column they belong to, rather than spreading
          across a sidebar the picker has no effect on. */}
      <div className={COLUMNS}>
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-xl font-bold tracking-tight">{t("leaderboard.title")}</h1>
          <div className="flex items-center gap-2">
            <RangePicker
              value={range}
              onChange={setRange}
              periods={periodData?.periods ?? []}
              currentPeriodIndex={periodData?.currentIndex}
              today={periodData?.today}
            />
            {isAdmin && <RefreshButton />}
          </div>
        </div>
      </div>

      {isError && (
        <Alert variant="destructive">
          <AlertCircleIcon />
          <AlertTitle>{t("leaderboard.loadFailed")}</AlertTitle>
          <AlertDescription>
            {describeError(error) ?? t("common.somethingWentWrong")}
          </AlertDescription>
        </Alert>
      )}

      {isLoading && !data ? (
        <LeaderboardSkeleton />
      ) : data ? (
        // Everything below reads the ranking metric from here rather than being
        // handed it: it decides which number leads a card, and threading it
        // through the podium, the rows and the stat tiles as a prop would put it
        // in the signature of every component between.
        <RankMetricProvider value={data.rankMetric}>
          <Loaded
            data={data}
            range={range}
            isAdmin={isAdmin}
            isStrategist={isStrategist}
            canSeeStrategist={canSeeStrategist}
          />
        </RankMetricProvider>
      ) : null}
    </div>
  );
}

function Loaded({
  data,
  range,
  isAdmin,
  isStrategist,
  canSeeStrategist,
}: {
  data: LeaderboardResponse;
  range: RangeSelection;
  isAdmin: boolean;
  isStrategist: boolean;
  canSeeStrategist: boolean;
}) {
  const { t } = useTranslation();
  // Impressions rather than the ranking metric: this asks whether anything ran
  // at all, and a board that served a million views on an exhausted budget has
  // plenty to show.
  const hasDelivery = data.standings.some((s) => s.impressions > 0);
  const showBonus = data.period !== null;
  /** Same period the range picker selected; rolling ranges fall through to current. */
  const strategistPeriod = data.period?.index;

  return (
    // A main column and a sidebar once there is room, stacked on a phone. The
    // column reads top-down: the period, the editor standings, then the top
    // videos of the range. The sidebar holds what runs on its own clock - the
    // prizes, then the week's new winners. The last row takes the slack, so a
    // sidebar taller than the column leaves its space under the footer rather
    // than between the standings and the videos.
    <div className={cn("flex flex-col gap-4 xl:grid-rows-[auto_1fr]", COLUMNS)}>
      <div className="flex min-w-0 flex-col gap-4 xl:col-start-1 xl:row-start-1">
        {/* Her own earnings open the page for her, and only for her. An admin
            gets the same card further down: it is something to check on, not
            the reason they came. */}
        {isStrategist && <StrategistPayout periodIndex={strategistPeriod} />}

        {/* When her payment sits above everything else, a titled break stops the
            editor board reading as a continuation of the same card. */}
        {isStrategist && (
          <div className="flex items-center gap-1.5 border-t px-1 pt-5">
            <TrophyIcon className="text-muted-foreground size-3.5" />
            <h2 className="text-sm font-semibold">{t("leaderboard.editorsSection")}</h2>
          </div>
        )}

        <PeriodBar data={data} />

        {/* The top videos open the board, under the pay-period clock, as they always have. */}
        {hasDelivery && (
          <>
            <Suspense fallback={<Skeleton className="h-80 w-full rounded-xl" />}>
              <TopVariants
                variants={data.topVariants}
                currency={data.currency}
                roasGreenThreshold={data.roasGreenThreshold}
                range={range}
                today={data.today}
              />
            </Suspense>
            <MoreVideos next={data.nextVariants ?? []} total={data.variantCount ?? 0} range={range} />
          </>
        )}

        {hasDelivery ? (
          <section className="flex min-w-0 flex-col gap-2.5" aria-label={t("leaderboard.standings")}>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 px-1">
              <TrophyIcon className="text-muted-foreground size-3.5 self-center" />
              <h2 className="text-sm font-semibold">{t("leaderboard.standings")}</h2>
              <span className="text-muted-foreground text-xs">
                {showBonus ? t("leaderboard.standingsBonus") : t("leaderboard.standingsRange")}
              </span>
            </div>

            <StandingsBoard
              standings={data.standings}
              terms={data.terms}
              currency={data.currency}
              range={range}
              showBonus={showBonus}
            />

            {showBonus && data.terms.mode !== "split" && (
              <p className="text-muted-foreground px-1 text-xs leading-snug text-pretty">
                {t("leaderboard.bonusExplainer", { context: data.metric })}
              </p>
            )}
          </section>
        ) : (
          <Empty className="border">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <TrophyIcon />
              </EmptyMedia>
              <EmptyTitle>{t("leaderboard.noDelivery")}</EmptyTitle>
              <EmptyDescription>
                {t("leaderboard.noDeliveryDescription")}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        )}
      </div>

      <aside className="flex min-w-0 flex-col gap-4 xl:col-start-2 xl:row-span-2 xl:row-start-1">
        {/* MacBook first, the newer prizes under it: the admins' order. */}
        <PrizeCards />
        <Suspense fallback={<Skeleton className="h-80 w-full rounded-xl" />}>
          <NewWinners range={range} />
        </Suspense>
      </aside>

      <div className="flex min-w-0 flex-col gap-4 xl:col-start-1 xl:row-start-2">
        {isAdmin && hasDelivery && <TeamSummary data={data} />}

        {canSeeStrategist && !isStrategist && (
          <StrategistPayout periodIndex={strategistPeriod} />
        )}

        <DataFreshness
          lastSyncedAt={data.lastSyncedAt}
          dataThrough={data.dataThrough}
        />
      </div>
    </div>
  );
}

/** Admin-only: editors should be looking at their own standing, not the pot. */
function TeamSummary({ data }: { data: LeaderboardResponse }) {
  const { t } = useTranslation();
  const { totals } = useMetrics();
  const { team, currency, roasGreenThreshold, unassigned } = data;

  return (
    <Card className="py-0">
      <CardContent className="flex flex-col gap-3 p-4">
        <span className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
          {t("leaderboard.teamTotal")}
        </span>
        {/* In the order the app is ranked, so the summary opens on the same
            number the standings above it were sorted by. */}
        <div className="grid grid-cols-4 gap-3">
          {totals.map((metric) => (
            <MetricChip
              key={metric.key}
              label={metric.label}
              icon={metric.icon}
              value={
                metric.key === "impressions" ? (
                  // The only one that is abbreviated away from its real value,
                  // so the only one worth a hover.
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>{metric.format(team, currency)}</span>
                    </TooltipTrigger>
                    <TooltipContent>{formatNumber(team.impressions)}</TooltipContent>
                  </Tooltip>
                ) : (
                  metric.format(team, currency)
                )
              }
              valueClassName={metric.colorOf?.(team, roasGreenThreshold)}
            />
          ))}
        </div>

        {unassigned.impressions > 0 && (
          <p className="text-muted-foreground border-t pt-3 text-xs">
            {t("leaderboard.unassigned", {
              count: unassigned.videoCount,
              impressions: formatCompact(unassigned.impressions),
            })}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function LeaderboardSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <Skeleton className="h-16 w-full rounded-xl" />
      {[0, 1, 2, 3].map((i) => (
        <Skeleton key={i} className="h-52 w-full rounded-xl" />
      ))}
    </div>
  );
}
